// ---------------------------------------------------------------------------
// Cold retry of a persisted AemetProvenance's failed windows — re-fetches
// just those date ranges from AEMET and patches the result into the
// project's ALREADY-SAVED series (precipitation/temperature/ETP), without
// touching any other date. Used by AemetProvenanceDialog.tsx, reachable any
// time after the original fetch — unlike aemetJobStore.ts's own
// retryFailedWindows, which only works while that download's full raw
// station data is still held in the live job's memory, this one only ever
// needs the failed windows themselves, so it works just as well minutes or
// weeks later, after the job that made them is long gone.
// ---------------------------------------------------------------------------
import { patchColumnValues, type DailySeries } from "../dailySeries.ts";
import {
  enumerateWindows,
  expandSourceRanges,
  fetchDailyValuesForWindows,
  fetchLaneCount,
  sourceRanges,
  type AemetDailyRecord,
  type DateRange,
  type FetchDailyValuesResult,
} from "./aemet.ts";
import type { LatLon } from "../geo.ts";
import { fetchRocioValues } from "../rocio.ts";
import { buildSeriesForMethod, type SeriesKind } from "../seriesByMethod.ts";
import type { AemetProvenance } from "../storage.ts";

/**
 * `fetchRocioValues`'s counterpart to `fetchDailyValuesForWindows` — ROCIO
 * itself has no windowing/pacing (see rocio.ts's own comment), so this just
 * fetches each window as its own request and merges them, treating any
 * window whose fetch fails as still-failed (rather than partially merging a
 * failed window's `NaN`-filled records, which `fetchRocioValues`' own catch
 * branch doesn't even return — it returns nothing for that window at all).
 */
async function fetchRocioForWindows(
  windows: DateRange[],
  cellIds: string[],
  opts: { signal?: AbortSignal } = {}
): Promise<FetchDailyValuesResult> {
  const byStationByDate = new Map<string, Map<string, AemetDailyRecord>>();
  const failedWindows: DateRange[] = [];
  for (const w of windows) {
    const r = await fetchRocioValues(w, cellIds, opts);
    if (r.failedWindows.length > 0) {
      failedWindows.push(w);
      continue;
    }
    for (const [cellId, byDate] of r.byStationByDate) {
      const merged = byStationByDate.get(cellId) ?? new Map<string, AemetDailyRecord>();
      for (const [date, record] of byDate) merged.set(date, record);
      byStationByDate.set(cellId, merged);
    }
  }
  return { byStationByDate, failedWindows };
}

export interface AemetSeriesTrio {
  precipitation: DailySeries | null;
  temperature: DailySeries | null;
  etp: DailySeries | null;
}

/**
 * `prev` is the already-persisted, already-grouped `AemetProvenance.sourceByDate`
 * (see its own comment, storage.ts) — `fresh` is a raw per-day map, but only
 * ever covering the handful of retried days (`retriedDates` below), never a
 * whole sub-basin's range. Expanding `prev` back to per-day here is cheap
 * for that same reason: one sub-basin, on demand, not the whole batch.
 */
function mergeSourceByDate(
  prev: Map<string | null, DateRange[]>,
  fresh: Map<string, string | null>,
  dateRange: DateRange
): Map<string | null, DateRange[]> {
  const merged = new Map([...expandSourceRanges(prev), ...fresh]);
  const dates = enumerateWindows(dateRange, 1).map((w) => w.start);
  return sourceRanges(merged, dates);
}

/**
 * Re-downloads `provenance.failedWindows` and patches the result into
 * `series` (only the retried dates — everything else stays exactly as it
 * was) and into a new `provenance` (updated `sourceByDate` for the retried
 * dates, `failedWindows` narrowed to whatever's still missing). `columnName`
 * is the sub-basin's own name — same column-matching convention as
 * `insertColumnInSeries`. `centroid` is needed for ETP (Hargreaves needs
 * latitude) — the caller already has it (EditSubBasinDialog.tsx computes it
 * from the sub-basin's GIS geometry for the original fetch button too).
 */
export async function retryAemetProvenance(
  columnName: string,
  centroid: LatLon,
  provenance: AemetProvenance,
  series: AemetSeriesTrio,
  opts: { onProgress?: (done: number, total: number) => void; signal?: AbortSignal } = {}
): Promise<{ series: AemetSeriesTrio; provenance: AemetProvenance }> {
  const windows = provenance.failedWindows;
  if (windows.length === 0) return { series, provenance };

  const codes = provenance.stationsUsed.map((s) => s.code);
  const retriedDates = windows.flatMap((w) => enumerateWindows(w, 1).map((d) => d.start));

  // "rocio"'s codes are grid cell ids, not AEMET station codes — re-fetching
  // them from AEMET (fetchDailyValuesForWindows) would silently find nothing.
  // See rocio.ts's module header comment for why this aggregates by
  // mean/min/max instead of blending, same as the original fetch/apply.
  const isRocio = provenance.method === "rocio";
  const retryResult = isRocio
    ? await fetchRocioForWindows(windows, codes, { signal: opts.signal })
    : await fetchLaneCount().then((laneCount) =>
        fetchDailyValuesForWindows(windows, codes, {
          onProgress: opts.onProgress,
          signal: opts.signal,
          laneCount,
        })
      );
  // Dispatches to the right series builder for `provenance.method`
  // ("rocio"/"idw"/nearest-fallback) — shared with the original fetch/apply
  // (aemetJobStore.ts's applyResult) via seriesByMethod.ts, instead of a
  // second hand-written copy of the same dispatch: a plain `!isRocio` check
  // here used to always take the nearest-station path even for an "idw"
  // provenance, silently mixing aggregation methods within the same column
  // on retry, before the two copies were merged into one.
  const forMethod = (kind: SeriesKind) =>
    buildSeriesForMethod(
      provenance.method ?? "nearest",
      kind,
      codes,
      provenance.stationsUsed,
      retryResult.byStationByDate,
      retriedDates,
      centroid.lat
    );
  const precip = forMethod("precip");
  const temp = forMethod("temp");
  const eto = forMethod("eto");

  function patch(s: DailySeries | null, built: { values: number[] }): DailySeries | null {
    if (!s) return s;
    return patchColumnValues(
      s,
      columnName,
      new Map(retriedDates.map((date, i) => [date, built.values[i]]))
    );
  }

  return {
    series: {
      precipitation: patch(series.precipitation, precip),
      temperature: patch(series.temperature, temp),
      etp: patch(series.etp, eto),
    },
    provenance: {
      ...provenance,
      sourceByDate: {
        precipitation: mergeSourceByDate(
          provenance.sourceByDate.precipitation,
          precip.sourceByDate,
          provenance.dateRange
        ),
        temperature: mergeSourceByDate(
          provenance.sourceByDate.temperature,
          temp.sourceByDate,
          provenance.dateRange
        ),
        etp: mergeSourceByDate(provenance.sourceByDate.etp, eto.sourceByDate, provenance.dateRange),
      },
      failedWindows: retryResult.failedWindows,
    },
  };
}
